import React, { useState, useEffect } from 'react';
import { 
    Card, Form, Input, Button, Alert, Space, Typography, 
    Divider, message, Spin, Tag, Row, Col 
} from 'antd';
import { 
    ApiOutlined, LinkOutlined, CheckCircleOutlined, 
    ExclamationCircleOutlined, SettingOutlined, GlobalOutlined 
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { setTrelloWebhook, testTrelloWebhook, getTrelloWebhookStatus } from '../actions';

const { Title, Text, Paragraph } = Typography;

const TrelloSettings = () => {
    const dispatch = useDispatch();
    const { loading } = useSelector(state => state.adminSettings);
    
    const [form] = Form.useForm();
    const [webhookUrl, setWebhookUrl] = useState('');
    const [boardId, setBoardId] = useState('68467946661f0fb890604ac0'); // Default board ID
    const [webhookStatus, setWebhookStatus] = useState('not_set'); // not_set, active, error
    const [testLoading, setTestLoading] = useState(false);
    const [webhookId, setWebhookId] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);

    useEffect(() => {
        // Load current webhook status on component mount
        loadWebhookStatus();
    }, []);

    const loadWebhookStatus = async () => {
        try {
            setStatusLoading(true);
            const result = await dispatch(getTrelloWebhookStatus());
            
            if (result.success && result.data) {
                const { webhooks, hasActiveWebhook } = result.data;
                
                if (hasActiveWebhook && webhooks.length > 0) {
                    const activeWebhook = webhooks.find(w => w.active);
                    if (activeWebhook) {
                        setWebhookStatus('active');
                        setWebhookUrl(activeWebhook.callbackURL || '');
                        setWebhookId(activeWebhook.id);
                        
                        // Set board ID from webhook data if available, otherwise use current state
                        const webhookBoardId = activeWebhook.idModel || activeWebhook.boardId || boardId;
                        setBoardId(webhookBoardId);
                        
                        form.setFieldsValue({ 
                            webhookUrl: activeWebhook.callbackURL || '',
                            boardId: webhookBoardId
                        });
                    }
                } else {
                    setWebhookStatus('not_set');
                }
            } else {
                setWebhookStatus('error');
            }
        } catch (error) {
            console.error('Error loading webhook status:', error);
            setWebhookStatus('error');
        } finally {
            setStatusLoading(false);
        }
    };

    const handleSetWebhook = async (values) => {
        try {
            const result = await dispatch(setTrelloWebhook({
                webhookUrl: values.webhookUrl,
                boardId: values.boardId || boardId
            }));
            
            if (result.success) {
                message.success('Trello webhook has been set up successfully!');
                setWebhookUrl(values.webhookUrl);
                setBoardId(values.boardId || boardId);
                setWebhookStatus('active');
                setWebhookId(result.data?.webhookId);
                
                // Reload status to get updated info
                setTimeout(() => loadWebhookStatus(), 1000);
            } else {
                message.error(result.error || 'Unable to set up Trello webhook');
            }
        } catch (error) {
            console.error('Error setting Trello webhook:', error);
            message.error('An error occurred while setting up webhook');
        }
    };

    const handleTestWebhook = async () => {
        try {
            setTestLoading(true);
            const result = await dispatch(testTrelloWebhook());
            
            if (result.success) {
                message.success('Webhook test successful! Check main worker logs.');
            } else {
                message.error(result.error || 'Webhook test failed');
            }
        } catch (error) {
            console.error('Error testing webhook:', error);
            message.error('An error occurred while testing webhook');
        } finally {
            setTestLoading(false);
        }
    };

    const getStatusColor = () => {
        switch (webhookStatus) {
            case 'active': return 'success';
            case 'error': return 'error';
            default: return 'default';
        }
    };

    const getStatusText = () => {
        switch (webhookStatus) {
            case 'active': return 'Active';
            case 'error': return 'Error';
            default: return 'Not Set';
        }
    };

    return (
        <div className="trello-settings">
            <Card 
                title={
                    <Space>
                        <ApiOutlined />
                        <Title level={4} style={{ margin: 0 }}>Trello Integration Settings</Title>
                    </Space>
                } 
                bordered={false}
            >
                {/* Current Status */}
                <Card 
                    size="small" 
                    title={
                        <Space>
                            <ApiOutlined />
                            Webhook & Board Trello Status
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                    extra={
                        <Button 
                            size="small" 
                            type="link" 
                            onClick={loadWebhookStatus}
                            loading={statusLoading}
                            style={{ padding: 0, height: 'auto' }}
                        >
                            Refresh Status
                        </Button>
                    }
                >
                    <Spin spinning={statusLoading}>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Text strong>Webhook:</Text>
                                    <Tag 
                                        color={getStatusColor()} 
                                        icon={webhookStatus === 'active' ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                                    >
                                        {getStatusText()}
                                    </Tag>
                                    {webhookId && (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Webhook ID: {webhookId}
                                        </Text>
                                    )}
                                    {webhookUrl && (
                                        <Text type="secondary" style={{ fontSize: 12, wordBreak: 'break-all' }}>
                                            URL: {webhookUrl}
                                        </Text>
                                    )}
                                </Space>
                            </Col>
                            <Col span={12}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Text strong>Trello Board:</Text>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Board ID: {boardId}
                                    </Text>
                                </Space>
                            </Col>
                        </Row>
                    </Spin>
                </Card>

                {/* Setup Form */}
                <Divider orientation="left">
                    <Space>
                        <SettingOutlined />
                        Webhook Setup
                    </Space>
                </Divider>

                <Alert
                    message="Setup Instructions"
                    description={
                        <div>
                            <Paragraph style={{ marginBottom: 8 }}>
                                1. Enter the webhook endpoint URL that Trello will send notifications to
                            </Paragraph>
                            <Paragraph style={{ marginBottom: 8 }}>
                                2. Enter the Board ID of the Trello board you want to monitor
                            </Paragraph>
                            <Paragraph style={{ marginBottom: 8 }}>
                                3. URL must be a public domain, accessible from outside
                            </Paragraph>
                            <Paragraph style={{ marginBottom: 0 }}>
                                4. Example: https://yourdomain.com/api/trello/webhook/receive
                            </Paragraph>
                        </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                />

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSetWebhook}
                    initialValues={{
                        webhookUrl: webhookUrl,
                        boardId: boardId
                    }}
                >
                    <Form.Item
                        label={
                            <Space>
                                <LinkOutlined />
                                Webhook URL
                            </Space>
                        }
                        name="webhookUrl"
                        rules={[
                            { required: true, message: 'Please enter webhook URL' },
                            { type: 'url', message: 'Invalid URL' },
                            { 
                                pattern: /^https:\/\//, 
                                message: 'URL must use HTTPS' 
                            }
                        ]}
                        extra="Endpoint URL where Trello will send webhook notifications"
                    >
                        <Input 
                            placeholder="https://yourdomain.com/api/trello/webhook/receive" 
                            prefix={<GlobalOutlined />}
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <Space>
                                <ApiOutlined />
                                Trello Board ID
                            </Space>
                        }
                        name="boardId"
                        rules={[
                            { required: true, message: 'Please enter Trello Board ID' },
                            { 
                                pattern: /^[a-zA-Z0-9]{24}$/, 
                                message: 'Board ID must be 24 alphanumeric characters' 
                            }
                        ]}
                        extra="ID of the Trello board you want to create webhook for"
                    >
                        <Input 
                            placeholder="68467946661f0fb890604ac0" 
                            prefix={<ApiOutlined />}
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button 
                                type="primary" 
                                htmlType="submit" 
                                loading={loading}
                                icon={<ApiOutlined />}
                            >
                                {webhookStatus === 'active' ? 'Update Webhook' : 'Setup Webhook'}
                            </Button>
                            
                            {webhookStatus === 'active' && (
                                <Button 
                                    type="default"
                                    onClick={handleTestWebhook}
                                    loading={testLoading}
                                    icon={<CheckCircleOutlined />}
                                >
                                    Test Webhook
                                </Button>
                            )}
                        </Space>
                    </Form.Item>
                </Form>

                {/* Information */}
                <Divider />
                
                <Alert
                    message="Important Information"
                    description={
                        <div>
                            <Paragraph style={{ marginBottom: 8 }}>
                                • Webhook will receive notifications when there are changes in the Trello board
                            </Paragraph>
                            <Paragraph style={{ marginBottom: 8 }}>
                                • Currently the system only logs webhook events, business logic processing not yet implemented
                            </Paragraph>
                            <Paragraph style={{ marginBottom: 8 }}>
                                • To check webhook functionality, view logs in the main worker service
                            </Paragraph>
                            <Paragraph style={{ marginBottom: 0 }}>
                                • Trello webhook endpoint: POST /api/trello/webhook/receive
                            </Paragraph>
                        </div>
                    }
                    type="warning"
                    showIcon
                />
            </Card>
        </div>
    );
};

export default TrelloSettings;